import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Crown } from "lucide-react";

import Landing from "@/pages/Landing";
import AuthCallback from "@/pages/AuthCallback";

const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const RoleSelect = lazy(() => import("@/pages/RoleSelect"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const Feed = lazy(() => import("@/pages/Feed"));
const Upload = lazy(() => import("@/pages/Upload"));
const Cities = lazy(() => import("@/pages/Cities"));
const Profile = lazy(() => import("@/pages/Profile"));
const Admin = lazy(() => import("@/pages/Admin"));
const OwnerLogin = lazy(() => import("@/pages/OwnerLogin"));
const Discover = lazy(() => import("@/pages/Discover"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("@/pages/PaymentCancel"));
const CityLanding = lazy(() => import("@/pages/CityLanding"));
const CityDirectory = lazy(() => import("@/pages/CityDirectory"));
const StateHub = lazy(() => import("@/pages/StateHub"));
const Terms = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.Terms })));
const Privacy = lazy(() => import("@/pages/Legal").then((m) => ({ default: m.Privacy })));
const CookieConsent = lazy(() => import("@/components/CookieConsent").then((m) => ({ default: m.CookieConsent })));

const Loader = () => (
  <div className="grain min-h-screen flex items-center justify-center">
    <Crown className="gold-text animate-pulse" size={34} strokeWidth={1.5} />
  </div>
);

const Protected = ({ children, role }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.role) return <Navigate to="/role" replace />;
  if (role && user.role !== role && user.role !== "admin" && user.role !== "owner")
    return <Navigate to="/leaderboard" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader />;
  if (user && user.role) {
    const dest = user.role === "woman" ? "/cities"
      : (user.role === "admin" || user.role === "owner") ? "/admin" : "/leaderboard";
    return <Navigate to={dest} replace />;
  }
  return children;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Suspense fallback={<Loader />}>
    <Routes>
      <Route path="/" element={<PublicOnly><Landing /></PublicOnly>} />
      <Route path="/city/:slug" element={<CityLanding />} />
      <Route path="/cities/all" element={<CityDirectory />} />
      <Route path="/cities/:state" element={<StateHub />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/owner" element={<PublicOnly><OwnerLogin /></PublicOnly>} />
      <Route path="/dashboard" element={<AuthCallback />} />
      <Route path="/role" element={<RoleSelect />} />

      <Route path="/leaderboard" element={<Protected><Leaderboard /></Protected>} />
      <Route path="/discover" element={<Protected><Discover /></Protected>} />
      <Route path="/feed" element={<Protected><Feed /></Protected>} />
      <Route path="/cities" element={<Protected role="woman"><Cities /></Protected>} />
      <Route path="/upload" element={<Protected role="woman"><Upload /></Protected>} />
      <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/admin" element={<Protected role="admin"><Admin /></Protected>} />

      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <AppRouter />
            <Suspense fallback={null}><CookieConsent /></Suspense>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
